/**
 * Frontend/Browser Logger Utility
 * - Provides structured logging for browser-based (frontend) code
 * - Use this logger throughout the web application for consistent client-side logs
 * - Not intended for backend/server-side logging
 **/

export interface LogObject {
  text?: string; // Custom log message
  value?: any; // Value to log (object, string, etc.)
  error?: object; // Error message (if any)
  location?: string; // Location where the log was generated
  level?: "debug" | "info" | "warn" | "error" | "log";
}

const isProd = process.env.APP_ENV === "prod";

// The active user role (patient, referrer, patient navigator), shared across all
// logger calls so every client-side log is attributable to a role once known.
let currentUserRole: string | undefined;

/**
 * Set the active user role to enrich subsequent logs. Pass undefined to clear.
 */
export const setLoggerUserRole = (role: string | undefined): void => {
  currentUserRole = role;
};

const logger = (() => {
  if (!isProd) {
    function formatLog({ text, value, error, location, level }: LogObject): Record<string, any> {
      return {
        level,
        text,
        value,
        error,
        location,
        userRole: currentUserRole,
      };
    }

    type LoggerMethod = (logObj: LogObject) => void;

    const createLoggerMethod = (level: string): LoggerMethod => {
      return (logObj) => {
        const formatted = Object.fromEntries(
          Object.entries(formatLog(logObj)).filter(([, v]) => {
            return v !== undefined;
          }),
        );
        console[`${level === "info" || level === "log" ? "log" : level}`](`[${level}]:`, formatted);
      };
    };

    return {
      debug: createLoggerMethod("debug"),
      info: createLoggerMethod("info"),
      warn: createLoggerMethod("warn"),
      error: createLoggerMethod("error"),
      log: createLoggerMethod("log"),
    };
  }

  // In production, logger methods are no-ops
  const noop = () => {};
  return {
    debug: noop,
    info: noop,
    warn: noop,
    error: noop,
    log: noop,
  };
})();

export default logger;
